/**
 * QuickTradeDialog — floating "Quick Trade" mini-form for discretionary accounts.
 *
 * - Ticker input
 * - Buy / Sell toggle
 * - Quantity input
 * - "Preview" → calls /api/rebalance/what-if
 * - "Submit for Approval" → creates a pending recommendation via /api/recommendations
 *
 * Only shown when the portfolio has at least one discretionary account.
 */
import { useState, useRef, useEffect } from 'react'
import { X, TrendingUp, TrendingDown, Loader2, AlertTriangle, CheckCircle2, Zap } from 'lucide-react'
import { clsx } from 'clsx'
import { postWhatIf, createRecommendation } from '@/lib/api'
import type { WhatIfResponse } from '@/lib/types'

function fmt(n: number) {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtPct(n: number) {
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}

type Side = 'BUY' | 'SELL'
type Phase = 'form' | 'previewing' | 'preview' | 'submitting' | 'done' | 'error'

interface Props {
  onClose: () => void
}

export default function QuickTradeDialog({ onClose }: Props) {
  const [ticker, setTicker]     = useState('')
  const [side, setSide]         = useState<Side>('BUY')
  const [quantity, setQuantity] = useState('')
  const [phase, setPhase]       = useState<Phase>('form')
  const [preview, setPreview]   = useState<WhatIfResponse | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  const dialogRef = useRef<HTMLDivElement>(null)
  const tickerRef = useRef<HTMLInputElement>(null)

  // Focus ticker input on mount
  useEffect(() => {
    tickerRef.current?.focus()
  }, [])

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Close on backdrop click
  function handleBackdrop(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onClose()
  }

  const qty = parseInt(quantity, 10)
  const isValid = ticker.trim().length > 0 && qty > 0

  async function handlePreview() {
    if (!isValid) return
    setPhase('previewing')
    setErrorMsg('')
    try {
      const result = await postWhatIf({
        action:   side.toLowerCase(),
        ticker:   ticker.trim().toUpperCase(),
        quantity: qty,
      })
      setPreview(result)
      setPhase('preview')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setErrorMsg(msg || 'Preview failed — check ticker and quantity.')
      setPhase('error')
    }
  }

  async function handleSubmit() {
    if (!isValid) return
    setPhase('submitting')
    setErrorMsg('')
    try {
      await createRecommendation({
        action:    side,
        ticker:    ticker.trim().toUpperCase(),
        quantity:  qty,
        title:     `${side} ${qty} × ${ticker.trim().toUpperCase()} (quick trade)`,
        reasoning: `Submitted via Quick Trade panel. Side: ${side}, Qty: ${qty}.`,
        confidence: 70,
        priority:   3,
      })
      setPhase('done')
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setErrorMsg(msg || 'Submission failed.')
      setPhase('error')
    }
  }

  function handleReset() {
    setPreview(null)
    setErrorMsg('')
    setPhase('form')
  }

  const busy = phase === 'previewing' || phase === 'submitting'

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Quick Trade"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md card rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-amber" aria-hidden />
            <h2 className="font-display font-semibold text-text-primary">Quick Trade</h2>
            <span className="badge badge-amber text-[10px] px-1.5 py-0.5 ml-1">PAPER</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost p-1"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">

          {/* Done state */}
          {phase === 'done' && (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 size={36} className="text-emerald" />
              <p className="font-semibold text-text-primary">Submitted for Approval</p>
              <p className="text-sm text-text-muted">
                {side} {qty} × {ticker.trim().toUpperCase()} is now pending review.
              </p>
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={handleReset} className="btn-secondary text-xs">
                  New Trade
                </button>
                <button type="button" onClick={onClose} className="btn-ghost text-xs">
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Error state */}
          {phase === 'error' && (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <AlertTriangle size={28} className="text-amber" />
              <p className="text-sm text-text-muted">{errorMsg}</p>
              <button type="button" onClick={handleReset} className="btn-secondary text-xs">
                Try Again
              </button>
            </div>
          )}

          {/* Form + Preview */}
          {(phase === 'form' || phase === 'previewing' || phase === 'preview') && (
            <>
              {/* Ticker */}
              <div className="space-y-1.5">
                <label className="text-xs text-text-muted uppercase tracking-wider" htmlFor="qt-ticker">
                  Ticker
                </label>
                <input
                  ref={tickerRef}
                  id="qt-ticker"
                  type="text"
                  value={ticker}
                  onChange={e => { setTicker(e.target.value.toUpperCase()); setPreview(null); setPhase('form') }}
                  placeholder="e.g. AAPL"
                  className="input font-mono"
                  disabled={busy}
                />
              </div>

              {/* Side toggle */}
              <div className="space-y-1.5">
                <span className="text-xs text-text-muted uppercase tracking-wider">Side</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setSide('BUY'); setPreview(null); setPhase('form') }}
                    disabled={busy}
                    className={clsx(
                      'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold border transition-colors',
                      side === 'BUY'
                        ? 'bg-emerald/20 border-emerald/40 text-emerald'
                        : 'bg-surface-card border-border text-text-muted hover:text-text-secondary',
                    )}
                  >
                    <TrendingUp size={14} aria-hidden />
                    Buy
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSide('SELL'); setPreview(null); setPhase('form') }}
                    disabled={busy}
                    className={clsx(
                      'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold border transition-colors',
                      side === 'SELL'
                        ? 'bg-rose/20 border-rose/40 text-rose-light'
                        : 'bg-surface-card border-border text-text-muted hover:text-text-secondary',
                    )}
                  >
                    <TrendingDown size={14} aria-hidden />
                    Sell
                  </button>
                </div>
              </div>

              {/* Quantity */}
              <div className="space-y-1.5">
                <label className="text-xs text-text-muted uppercase tracking-wider" htmlFor="qt-qty">
                  Quantity (shares)
                </label>
                <input
                  id="qt-qty"
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={e => { setQuantity(e.target.value); setPreview(null); setPhase('form') }}
                  placeholder="e.g. 10"
                  className="input font-mono"
                  disabled={busy}
                />
              </div>

              {/* What-if preview panel */}
              {preview && phase === 'preview' && (
                <div className="bg-surface-hover border border-border rounded-xl p-4 space-y-3">
                  <div className="panel-title mb-1">Impact Preview</div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] text-text-muted">Portfolio before</div>
                      <div className="font-mono text-sm font-semibold text-text-primary">
                        {fmt(preview.before.total_value)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted">Portfolio after</div>
                      <div className="font-mono text-sm font-semibold text-text-primary">
                        {fmt(preview.after.total_value)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted">Trade value</div>
                      <div className="font-mono text-sm font-semibold text-text-primary">
                        {fmt(preview.trade_value)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted">Net change</div>
                      <div className={clsx(
                        'font-mono text-sm font-semibold',
                        preview.after.total_value >= preview.before.total_value
                          ? 'text-emerald' : 'text-rose-light',
                      )}>
                        {fmtPct(
                          ((preview.after.total_value - preview.before.total_value) /
                            Math.max(preview.before.total_value, 1)) * 100,
                        )}
                      </div>
                    </div>
                  </div>

                  {preview.concentration_warnings.length > 0 && (
                    <div className="flex items-start gap-2 bg-amber/10 border border-amber/20 rounded-lg p-2.5">
                      <AlertTriangle size={13} className="text-amber shrink-0 mt-0.5" />
                      <div className="space-y-0.5">
                        {preview.concentration_warnings.map((w, i) => (
                          <p key={i} className="text-xs text-amber-light">{w.message}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                {phase !== 'preview' ? (
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={!isValid || busy}
                    className="flex-1 btn-secondary text-sm gap-1.5 justify-center"
                  >
                    {phase === 'previewing' ? (
                      <><Loader2 size={13} className="animate-spin" /> Calculating…</>
                    ) : (
                      'Preview'
                    )}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleReset}
                      className="btn-ghost text-sm px-4"
                      disabled={busy}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={busy}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
                        side === 'BUY'
                          ? 'bg-emerald/25 border-emerald/40 text-emerald-light hover:bg-emerald/35'
                          : 'bg-rose/25 border-rose/40 text-rose-light hover:bg-rose/35',
                      )}
                    >
                      {busy ? (
                        <><Loader2 size={13} className="animate-spin" /> Submitting…</>
                      ) : (
                        `Submit ${side} for Approval`
                      )}
                    </button>
                  </>
                )}
              </div>

              <p className="text-[10px] text-text-muted text-center">
                Discretionary accounts only · Paper mode · Requires Unman's approval
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
